import React, { useMemo } from "react";
import { ChevronDown, ChevronUp, Download, Mail, Phone, Linkedin, User2 } from "lucide-react";
import {
  coalesce,
  formatCurrencyCompact,
  formatRange,
  formatPercentCompact,
  formatScore,
  humanize,
  isMissing,
  normalizeBoolean,
} from "./supplierFormat";

const cellBase = {
  padding: "10px 12px",
  verticalAlign: "top",
  borderBottom: "1px solid rgba(31,41,55,0.85)",
};

const headerCellBase = {
  padding: "10px 12px",
  textAlign: "left",
  fontSize: 11,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#9ca3af",
  borderBottom: "1px solid rgba(31,41,55,1)",
};

const naStyle = {
  fontStyle: "italic",
  color: "#6b7280",
};

const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "3px 8px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
  backgroundColor: "rgba(99,102,241,0.15)",
  border: "1px solid rgba(99,102,241,0.35)",
  color: "#c7d2fe",
};

const scorePillStyle = (score) => {
  const num = score === null ? null : Number(score);
  const clamped = Number.isFinite(num) ? Math.max(0, Math.min(1, num)) : null;
  const color =
    clamped === null
      ? "#64748b"
      : clamped >= 0.7
        ? "#22c55e"
        : clamped >= 0.55
          ? "#f59e0b"
          : "#f43f5e";

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 56,
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    color: "#020617",
    backgroundColor: color,
    boxShadow: "0 10px 24px rgba(0,0,0,0.25)",
  };
};

const getSupplierName = (row) =>
  coalesce(row, [
    "Supplier_name",
    "canonical_supplier_name",
    "supplier_name",
    "name",
    "Supplier Name",
  ]);

const getRegion = (row) => coalesce(row, ["Region", "region"]);

const getL1 = (row) => coalesce(row, ["L1", "l1"]);
const getL2 = (row) => coalesce(row, ["L2", "l2"]);
const getL3 = (row) => coalesce(row, ["L3", "l3"]);

const getContactEmail = (row) =>
  coalesce(row, ["contact_poc_email", "contact_email"]);

const getContactName = (row) => coalesce(row, ["contact_poc_name"]);
const getContactDesignation = (row) =>
  coalesce(row, ["contact_poc_designation"]);
const getContactRelevance = (row) => coalesce(row, ["contact_poc_relevance"]);

const getContactPhone = (row) => coalesce(row, ["contact_phone"]);
const getContactLinkedin = (row) => coalesce(row, ["contact_linkedin"]);

export const SupplierResultsTable = ({
  rows = [],
  turnKey,
  selectedRowIds,
  expandedRowIds,
  onToggleRowSelected,
  onToggleAllSelected,
  onToggleExpanded,
  downloadUrl,
  downloadLabel,
}) => {
  const ids = useMemo(
    () => rows.map((_r, idx) => `${turnKey}-${idx}`),
    [rows, turnKey]
  );

  const allSelected =
    ids.length > 0 && ids.every((id) => selectedRowIds.has(id));
  const anySelected = ids.some((id) => selectedRowIds.has(id));

  return (
    <div
      style={{
        borderRadius: 16,
        backgroundColor: "#020617",
        border: "1px solid rgba(31,41,55,1)",
        overflow: "hidden",
      }}
    >
      {(downloadUrl || downloadLabel) && (
        <div
          style={{
            padding: "10px 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            backgroundColor: "rgba(15,23,42,0.85)",
            borderBottom: "1px solid rgba(31,41,55,1)",
          }}
        >
          <div style={{ fontSize: 12, color: "#cbd5e1" }}>
            {downloadLabel || "Supplier list"}
          </div>
          {downloadUrl && (
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(99,102,241,0.45)",
                backgroundColor: "rgba(99,102,241,0.15)",
                color: "#e0e7ff",
                fontSize: 12,
                textDecoration: "none",
              }}
            >
              <Download size={14} />
              Download CSV
            </a>
          )}
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ backgroundColor: "rgba(15,23,42,0.85)" }}>
            <tr>
              <th style={{ ...headerCellBase, width: 44 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (!el) return;
                    el.indeterminate = !allSelected && anySelected;
                  }}
                  onChange={(e) => onToggleAllSelected(ids, e.target.checked)}
                />
              </th>
              <th style={{ ...headerCellBase, minWidth: 260 }}>Supplier</th>
              <th style={{ ...headerCellBase, minWidth: 120 }}>Region</th>
              <th style={{ ...headerCellBase, minWidth: 220 }}>Category</th>
              <th style={{ ...headerCellBase, minWidth: 140 }}>Total Spend</th>
              <th style={{ ...headerCellBase, minWidth: 140 }}>
                Unique Clients
              </th>
              <th style={{ ...headerCellBase, minWidth: 160 }}>
                Slice Share (Q1–Q3)
              </th>
              <th style={{ ...headerCellBase, minWidth: 120 }}>Score</th>
              <th style={{ ...headerCellBase, width: 80, textAlign: "center" }}>
                Details
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const rowId = ids[idx];
              const supplierName = getSupplierName(row);

              const region = getRegion(row);
              const l1 = getL1(row);
              const l2 = getL2(row);
              const l3 = getL3(row);

              const spend = coalesce(row, ["spend_total"]);
              const uniqueClients = coalesce(row, ["num_unique_clients"]);
              const q1 = coalesce(row, ["client_slice_share_q1"]);
              const q3 = coalesce(row, ["client_slice_share_q3"]);
              const weightedScore = coalesce(row, ["weighted_score"]);
              const isHead = normalizeBoolean(coalesce(row, ["is_head_supplier"]));

              const categoryParts = [l1, l2, l3]
                .filter((p) => !isMissing(p))
                .map((p) => humanize(p));
              const categoryLabel = categoryParts.length
                ? categoryParts.join(" \u203A ")
                : null;

              const spendLabel = formatCurrencyCompact(spend);
              const sliceRange = formatRange(q1, q3);
              const scoreLabel = formatScore(weightedScore);

              const expanded = expandedRowIds.has(rowId);

              const renderValue = (value) => {
                if (isMissing(value)) return <span style={naStyle}>N/A</span>;
                return <span style={{ color: "#e5e7eb" }}>{value}</span>;
              };

              return (
                <React.Fragment key={rowId}>
                  <tr style={{ backgroundColor: "#020617" }}>
                    <td style={cellBase}>
                      <input
                        type="checkbox"
                        checked={selectedRowIds.has(rowId)}
                        onChange={() => onToggleRowSelected(rowId)}
                      />
                    </td>
                    <td style={cellBase}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ color: "#f8fafc", fontWeight: 600 }}>
                          {isMissing(supplierName) ? "Unnamed supplier" : supplierName}
                        </span>
                        {isHead === true && <span style={badgeStyle}>Head</span>}
                      </div>
                    </td>
                    <td style={cellBase}>
                      {renderValue(isMissing(region) ? null : humanize(region))}
                    </td>
                    <td style={cellBase}>{renderValue(categoryLabel)}</td>
                    <td style={cellBase}>{renderValue(spendLabel)}</td>
                    <td style={cellBase}>
                      {renderValue(isMissing(uniqueClients) ? null : uniqueClients)}
                    </td>
                    <td style={cellBase}>{renderValue(sliceRange)}</td>
                    <td style={cellBase}>
                      {isMissing(scoreLabel) ? (
                        <span style={naStyle}>N/A</span>
                      ) : (
                        <span style={scorePillStyle(weightedScore)}>
                          {scoreLabel}
                        </span>
                      )}
                    </td>
                    <td style={{ ...cellBase, textAlign: "center" }}>
                      <button
                        type="button"
                        onClick={() => onToggleExpanded(rowId)}
                        style={{
                          border: "1px solid rgba(148,163,184,0.25)",
                          background: "rgba(2,6,23,0.55)",
                          cursor: "pointer",
                          padding: "6px 8px",
                          borderRadius: 999,
                          color: "#cbd5e1",
                        }}
                      >
                        {expanded ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                      </button>
                    </td>
                  </tr>
                  {expanded && (
                    <tr style={{ backgroundColor: "rgba(15,23,42,0.6)" }}>
                      <td
                        colSpan={9}
                        style={{
                          padding: "14px 14px",
                          borderBottom: "1px solid rgba(31,41,55,0.85)",
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                            gap: 12,
                          }}
                        >
                          <div
                            style={{
                              borderRadius: 14,
                              padding: 12,
                              backgroundColor: "rgba(2,6,23,0.7)",
                              border: "1px solid rgba(148,163,184,0.15)",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                color: "#94a3b8",
                                fontSize: 12,
                                marginBottom: 8,
                              }}
                            >
                              <User2 size={14} />
                              <span style={{ fontWeight: 700 }}>Contact</span>
                            </div>
                            <div style={{ fontSize: 13, color: "#e5e7eb" }}>
                              <div>
                                {renderValue(
                                  [getContactName(row), getContactDesignation(row)]
                                    .filter((v) => !isMissing(v))
                                    .join(" · ") || null
                                )}
                              </div>
                              {!isMissing(getContactRelevance(row)) && (
                                <div style={{ marginTop: 6, color: "#cbd5e1" }}>
                                  <span style={{ color: "#94a3b8" }}>
                                    Relevance:
                                  </span>{" "}
                                  {getContactRelevance(row)}
                                </div>
                              )}
                              <div
                                style={{
                                  display: "flex",
                                  gap: 10,
                                  flexWrap: "wrap",
                                  marginTop: 10,
                                }}
                              >
                                {!isMissing(getContactEmail(row)) && (
                                  <a
                                    href={`mailto:${getContactEmail(row)}`}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 6,
                                      padding: "6px 10px",
                                      borderRadius: 999,
                                      border:
                                        "1px solid rgba(219,234,254,0.25)",
                                      backgroundColor: "rgba(59,130,246,0.15)",
                                      color: "#dbeafe",
                                      textDecoration: "none",
                                      fontSize: 12,
                                    }}
                                  >
                                    <Mail size={14} />
                                    {getContactEmail(row)}
                                  </a>
                                )}
                                {!isMissing(getContactPhone(row)) && (
                                  <a
                                    href={`tel:${getContactPhone(row)}`}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 6,
                                      padding: "6px 10px",
                                      borderRadius: 999,
                                      border:
                                        "1px solid rgba(187,247,208,0.25)",
                                      backgroundColor: "rgba(34,197,94,0.15)",
                                      color: "#bbf7d0",
                                      textDecoration: "none",
                                      fontSize: 12,
                                    }}
                                  >
                                    <Phone size={14} />
                                    {getContactPhone(row)}
                                  </a>
                                )}
                                {!isMissing(getContactLinkedin(row)) && (
                                  <a
                                    href={getContactLinkedin(row)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 6,
                                      padding: "6px 10px",
                                      borderRadius: 999,
                                      border:
                                        "1px solid rgba(199,210,254,0.25)",
                                      backgroundColor: "rgba(99,102,241,0.15)",
                                      color: "#c7d2fe",
                                      textDecoration: "none",
                                      fontSize: 12,
                                    }}
                                  >
                                    <Linkedin size={14} />
                                    View LinkedIn
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>

                          <div
                            style={{
                              borderRadius: 14,
                              padding: 12,
                              backgroundColor: "rgba(2,6,23,0.7)",
                              border: "1px solid rgba(148,163,184,0.15)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 12,
                                color: "#94a3b8",
                                fontWeight: 700,
                                marginBottom: 8,
                              }}
                            >
                              Metrics snapshot
                            </div>

                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                gap: 10,
                                fontSize: 13,
                              }}
                            >
                              <div>
                                <div style={{ color: "#94a3b8", fontSize: 12 }}>
                                  Revenue bucket
                                </div>
                                <div style={{ marginTop: 4 }}>
                                  {renderValue(
                                    isMissing(coalesce(row, ["revenue_bucket"]))
                                      ? null
                                      : humanize(coalesce(row, ["revenue_bucket"]))
                                  )}
                                </div>
                              </div>
                              <div>
                                <div style={{ color: "#94a3b8", fontSize: 12 }}>
                                  Spend share
                                </div>
                                <div style={{ marginTop: 4 }}>
                                  {renderValue(
                                    formatPercentCompact(
                                      coalesce(row, ["supplier_spend_share"])
                                    )
                                  )}
                                </div>
                              </div>
                              <div>
                                <div style={{ color: "#94a3b8", fontSize: 12 }}>
                                  Client percentage
                                </div>
                                <div style={{ marginTop: 4 }}>
                                  {renderValue(
                                    formatPercentCompact(
                                      coalesce(row, ["client_percentage"])
                                    )
                                  )}
                                </div>
                              </div>
                              <div>
                                <div style={{ color: "#94a3b8", fontSize: 12 }}>
                                  Source
                                </div>
                                <div style={{ marginTop: 4 }}>
                                  {renderValue(coalesce(row, ["source"]))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
